export type SafetyTemplateStatus = 'draft' | 'pending_approval' | 'approved' | 'active' | 'archived';
export type SafetyInstructionType = 'initial' | 'annual' | 'incident' | 'site' | 'special_critical_area';
export type SafetyTargetGroup = 'monteure' | 'buero' | 'lwl_team' | 'kupfer_team' | 'fahrer' | 'lager' | 'bauleiter' | 'azubis' | 'all' | 'individual';
export type SafetyQuestionType = 'yes_no' | 'single_choice' | 'multi_choice' | 'free_text' | 'mandatory_confirmation' | 'acknowledgement';

export interface SafetyTemplate {
  id: string;
  title: string;
  description: string | null;
  instruction_type: SafetyInstructionType;
  target_group: SafetyTargetGroup;
  individual_employee_ids: string[] | null;
  version: number;
  status: SafetyTemplateStatus;
  legal_basis: string[];
  repeat_interval_months: number;
  passing_score_percent: number;
  max_attempts: number;
  requires_followup_resolution: boolean;
  responsible_admin_id: string | null;
  approved_by: string | null;
  approved_at: string | null;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean;
  parent_template_id: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

export interface SafetyCategory {
  id: string;
  template_id: string | null;
  title: string;
  description: string | null;
  sort_order: number;
  is_required: boolean;
  is_active: boolean;
  is_library: boolean;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

export interface SafetyItem {
  id: string;
  category_id: string;
  title: string;
  content: string | null;
  risk_description: string | null;
  protective_measures: string | null;
  employee_behavior: string | null;
  employee_confirmation_text: string | null;
  minimum_read_time_seconds: number;
  image_url: string | null;
  attachment_url: string | null;
  external_link: string | null;
  sort_order: number;
  is_required: boolean;
  is_active: boolean;
  version: number;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

export interface SafetyQuestion {
  id: string;
  item_id: string | null;
  category_id: string | null;
  question: string;
  question_type: SafetyQuestionType;
  answer_options: Array<{ id?: string; label: string }> | unknown;
  correct_answer: unknown;
  explanation: string | null;
  requires_manual_review: boolean;
  is_required: boolean;
  is_scored: boolean;
  difficulty: number;
  sort_order: number;
  is_active: boolean;
  version: number;
}

export const STATUS_LABEL: Record<SafetyTemplateStatus, string> = {
  draft: 'Entwurf',
  pending_approval: 'Zur Freigabe',
  approved: 'Freigegeben',
  active: 'Aktiv',
  archived: 'Archiviert',
};

export const STATUS_VARIANT: Record<SafetyTemplateStatus, 'secondary' | 'default' | 'outline' | 'destructive'> = {
  draft: 'outline',
  pending_approval: 'secondary',
  approved: 'default',
  active: 'default',
  archived: 'destructive',
};

export const TYPE_LABEL: Record<SafetyInstructionType, string> = {
  initial: 'Erstunterweisung',
  annual: 'Jahresunterweisung',
  incident: 'Anlassunterweisung',
  site: 'Baustellenunterweisung',
  special_critical_area: 'Sonderunterweisung kritischer Bereich',
};

export const TARGET_GROUP_LABEL: Record<SafetyTargetGroup, string> = {
  monteure: 'Monteure',
  buero: 'Büro',
  lwl_team: 'LWL-Team',
  kupfer_team: 'Kupfer-Team',
  fahrer: 'Fahrer',
  lager: 'Lager',
  bauleiter: 'Bauleiter',
  azubis: 'Azubis',
  all: 'Alle Mitarbeiter',
  individual: 'Individuelle Auswahl',
};

export const QUESTION_TYPE_LABEL: Record<SafetyQuestionType, string> = {
  yes_no: 'Ja/Nein',
  single_choice: 'Single Choice',
  multi_choice: 'Multiple Choice',
  free_text: 'Freitext',
  mandatory_confirmation: 'Pflichtbestätigung',
  acknowledgement: 'Kenntnisnahme',
};
